import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Globe, Menu, X, ChevronDown, BookOpen, Users, Shield, Leaf, Heart, GraduationCap } from 'lucide-react';

export const Navbar = () => {
  const { language, changeLanguage, t } = useLanguage();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isEcosystemOpen, setIsEcosystemOpen] = useState(false);
  const [isStrategicOpen, setIsStrategicOpen] = useState(false);

  // Labels multilingues
  const labels = {
    ecosystemLabel: {
      fr: "Écosystème de Souveraineté",
      en: "Sovereignty Ecosystem",
      pt: "Ecossistema de Soberania",
      it: "Ecosistema di Sovranità",
      ru: "Экосистема суверенитета",
      zh: "主权生态系统"
    },
    strategicLabel: {
      fr: "Preuves & Expertise",
      en: "Proofs & Expertise",
      pt: "Provas & Expertise",
      it: "Prove & Competenza",
      ru: "Доказательства",
      zh: "证明与专业"
    }
  };

  // Écosystème de Souveraineté (regroupement des 3 pôles)
  const ecosystemLinks = [
    { 
      path: '/health', 
      icon: Heart,
      color: 'sky',
      label: {
        fr: "Santé – PICSU",
        en: "Health – PICSU",
        pt: "Saúde – PICSU",
        it: "Salute – PICSU",
        ru: "Здоровье – PICSU",
        zh: "健康 – PICSU"
      },
      desc: {
        fr: "Programme d'Implémentation de la Couverture Santé Universelle",
        en: "Universal Health Coverage Implementation Program",
        pt: "Programa de Implementação da Cobertura Universal de Saúde",
        it: "Programma di Implementazione della Copertura Sanitaria Universale",
        ru: "Программа внедрения всеобщего охвата услугами здравоохранения",
        zh: "全民健康覆盖实施计划"
      }
    },
    { 
      path: '/agriculture', 
      icon: Leaf,
      color: 'emerald',
      label: {
        fr: "Agriculture – French Method",
        en: "Agriculture – French Method",
        pt: "Agricultura – French Method",
        it: "Agricoltura – French Method",
        ru: "Сельское хозяйство – French Method",
        zh: "农业 – French Method"
      },
      desc: {
        fr: "Souveraineté alimentaire et entrepreneuriat agricole",
        en: "Food sovereignty and agricultural entrepreneurship",
        pt: "Soberania alimentar e empreendedorismo agrícola",
        it: "Sovranità alimentare e imprenditorialità agricola",
        ru: "Продовольственный суверенитет и сельскохозяйственное предпринимательство",
        zh: "粮食主权和农业创业"
      }
    },
    { 
      path: '/education', 
      icon: GraduationCap,
      color: 'amber',
      label: {
        fr: "Éducation – Réarmement Moral",
        en: "Education – Moral Rearmament",
        pt: "Educação – Rearmamento Moral",
        it: "Educazione – Riarmo Morale",
        ru: "Образование – Моральное перевооружение",
        zh: "教育 – 道德重塑"
      },
      desc: {
        fr: "Formation du capital humain africain",
        en: "African human capital formation",
        pt: "Formação do capital humano africano",
        it: "Formazione del capitale umano africano",
        ru: "Формирование африканского человеческого капитала",
        zh: "非洲人力资本培养"
      }
    },
  ];

  // Preuves & Expertise
  const strategicLinks = [
    { 
      path: '/book-picsu', 
      icon: BookOpen,
      label: {
        fr: "Ouvrage PICSU",
        en: "PICSU Book",
        pt: "Livro PICSU",
        it: "Libro PICSU",
        ru: "Книга PICSU",
        zh: "PICSU著作"
      }
    },
    { 
      path: '/scientific-council', 
      icon: Users,
      label: {
        fr: "Conseil Scientifique (PNS)",
        en: "Scientific Council (PNS)",
        pt: "Conselho Científico (PNS)",
        it: "Consiglio Scientifico (PNS)",
        ru: "Научный совет (PNS)",
        zh: "科学委员会 (PNS)"
      }
    },
    { 
      path: '/validations', 
      icon: Shield,
      label: {
        fr: "Mur de Preuves",
        en: "Wall of Proof",
        pt: "Muro de Provas",
        it: "Muro delle Prove",
        ru: "Стена доказательств",
        zh: "证明墙"
      }
    },
  ];

  const mainNavLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/investors', label: t('nav.investors') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const languages = [
    { code: 'fr', label: 'Français' },
    { code: 'en', label: 'English' },
    { code: 'pt', label: 'Português' },
    { code: 'it', label: 'Italiano' },
    { code: 'ru', label: 'Русский' },
    { code: 'zh', label: '中文' },
  ];

  const isEcosystemActive = ['/health', '/agriculture', '/education'].includes(location.pathname);
  const isStrategicActive = ['/book-picsu', '/scientific-council', '/validations'].includes(location.pathname);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/cfjr0roy_loglenodaFR.png" 
              alt="LENODA Logo" 
              className="h-14 w-auto"
            />
          </Link>

          <div className="hidden xl:flex items-center gap-1">
            {mainNavLinks.slice(0, 2).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.slice(1) || 'home'}`}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  location.pathname === link.path
                    ? 'text-sky-600 bg-sky-50'
                    : 'text-slate-700 hover:text-sky-600 hover:bg-slate-50'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Menu Écosystème de Souveraineté */}
            <div className="relative">
              <button
                onClick={() => { setIsEcosystemOpen(!isEcosystemOpen); setIsStrategicOpen(false); }}
                onBlur={() => setTimeout(() => setIsEcosystemOpen(false), 200)}
                data-testid="ecosystem-menu-button"
                className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isEcosystemActive
                    ? 'text-emerald-600 bg-emerald-50'
                    : 'text-slate-700 hover:text-emerald-600 hover:bg-emerald-50'
                }`}
              >
                <Globe className="w-4 h-4" />
                {labels.ecosystemLabel[language] || labels.ecosystemLabel.fr}
                <ChevronDown className={`w-4 h-4 transition-transform ${isEcosystemOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isEcosystemOpen && (
                <div className="absolute left-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50" data-testid="ecosystem-dropdown">
                  <div className="px-4 py-2 border-b border-slate-100">
                    <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">
                      {language === 'fr' ? "L'Écosystème Global de Souveraineté Africaine" :
                       language === 'en' ? "The Global African Sovereignty Ecosystem" :
                       language === 'pt' ? "O Ecossistema Global de Soberania Africana" :
                       language === 'it' ? "L'Ecosistema Globale di Sovranità Africana" :
                       language === 'ru' ? "Глобальная экосистема африканского суверенитета" :
                       "非洲主权全球生态系统"}
                    </p>
                  </div>
                  {ecosystemLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      data-testid={`ecosystem-link-${link.path.slice(1)}`}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                      onClick={() => setIsEcosystemOpen(false)}
                    >
                      <div className={`w-10 h-10 bg-${link.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <link.icon className={`w-5 h-5 text-${link.color}-600`} />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">{link.label[language] || link.label.fr}</p>
                        <p className="text-xs text-slate-500">{link.desc[language] || link.desc.fr}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Menu Preuves & Expertise */}
            <div className="relative">
              <button
                onClick={() => { setIsStrategicOpen(!isStrategicOpen); setIsEcosystemOpen(false); }}
                onBlur={() => setTimeout(() => setIsStrategicOpen(false), 200)}
                data-testid="strategic-menu-button"
                className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isStrategicActive
                    ? 'text-amber-600 bg-amber-50'
                    : 'text-slate-700 hover:text-amber-600 hover:bg-amber-50'
                }`}
              >
                <Shield className="w-4 h-4" />
                {labels.strategicLabel[language] || labels.strategicLabel.fr}
                <ChevronDown className={`w-4 h-4 transition-transform ${isStrategicOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isStrategicOpen && (
                <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50" data-testid="strategic-dropdown">
                  {strategicLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      data-testid={`strategic-link-${link.path.slice(1)}`}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                      onClick={() => setIsStrategicOpen(false)}
                    >
                      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                        <link.icon className="w-4 h-4 text-amber-600" />
                      </div>
                      <span className="font-medium">{link.label[language] || link.label.fr}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {mainNavLinks.slice(2).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.slice(1) || 'home'}`}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  location.pathname === link.path
                    ? 'text-sky-600 bg-sky-50'
                    : 'text-slate-700 hover:text-sky-600 hover:bg-slate-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                data-testid="language-switcher-button"
                className="flex items-center gap-2 px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
              >
                <Globe className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-700 uppercase">{language}</span>
              </button>
              {isLangOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50" data-testid="language-dropdown">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setIsLangOpen(false);
                      }}
                      data-testid={`language-option-${lang.code}`}
                      className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                        language === lang.code
                          ? 'bg-sky-50 text-sky-600 font-medium'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/deal-room"
              data-testid="nav-deal-room-cta"
              className="hidden lg:block px-5 py-2.5 bg-emerald-600 text-white rounded-full text-sm font-medium hover:bg-emerald-700 transition-colors"
            >
              {t('nav.dealRoom')}
            </Link>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-button"
              className="xl:hidden p-2 text-slate-700"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="xl:hidden py-4 border-t border-slate-200" data-testid="mobile-menu">
            {mainNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                data-testid={`mobile-nav-link-${link.path.slice(1) || 'home'}`}
                className={`block py-3 text-base font-medium ${
                  location.pathname === link.path
                    ? 'text-sky-600'
                    : 'text-slate-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Ecosystem links in mobile */}
            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">
                {labels.ecosystemLabel[language] || labels.ecosystemLabel.fr}
              </p>
              {ecosystemLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 py-3 text-base font-medium text-slate-700"
                >
                  <link.icon className="w-5 h-5 text-emerald-600" />
                  {link.label[language] || link.label.fr}
                </Link>
              ))}
            </div>
            
            {/* Strategic links in mobile */}
            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-2">
                {labels.strategicLabel[language] || labels.strategicLabel.fr}
              </p>
              {strategicLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 py-3 text-base font-medium text-slate-700"
                >
                  <link.icon className="w-5 h-5 text-amber-600" />
                  {link.label[language] || link.label.fr}
                </Link>
              ))}
            </div>
            
            <Link
              to="/deal-room"
              onClick={() => setIsMenuOpen(false)}
              data-testid="mobile-deal-room-link"
              className="block mt-4 px-6 py-3 bg-emerald-600 text-white rounded-full text-center font-medium"
            >
              {t('nav.dealRoom')}
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};
